import {
	CommonModule,
	CurrencyPipe,
	registerLocaleData,
} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import type {
	OnChanges,
	Signal,
	SimpleChange,
	SimpleChanges,
} from '@angular/core';
import {
	Component,
	computed,
	effect,
	EventEmitter,
	inject,
	Input,
	input,
	Output,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';

import { LetDirective } from '@ngrx/component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import type { Instance as FlatPickrInstance } from 'flatpickr/dist/types/instance';
import type { BaseOptions as FlatPickrBaseOptions } from 'flatpickr/dist/types/options';
import type { Observable } from 'rxjs';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	map,
	tap,
} from 'rxjs';

import type { AvailabilityDatesInterface } from '@valk-nx/availability-dates-store/availability-dates.interface';
import type { FlatPickrEvent } from '@valk-nx/components/ui-calendar/src/lib/calendar';
import {
	CalendarComponent,
	FlatPickrEventType,
} from '@valk-nx/components/ui-calendar/src/lib/calendar';
import { addOptionalPricing } from '@valk-nx/components/ui-date-picker/src/lib/date-picker.helper';
import type { DatePickerInterface } from '@valk-nx/components/ui-date-picker/src/lib/date-picker.interface';
import { StepperComponent } from '@valk-nx/components/ui-stepper/src/lib/stepper';
import { cn } from '@valk-nx/core/lib/core';
import { FlatpickrFacade } from '@valk-nx/flatpickr-store/flatpickr.facade';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

import {
	isArrivalDateValid,
	isDepartureDateAvailable as isDepartureDateAvailableHelper,
} from '../availability-date-validation.helper';
import { AvailabilityWidgetInputComponent } from '../availability-input/availability-input';

import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';

registerLocaleData(localeNl, 'nl');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEs, 'es');

interface DepartureDateRange {
	min: string;
	max: string;
}

type DateBound = Partial<FlatPickrBaseOptions>['minDate'];

function isSameInstant(a: DateBound, b: DateBound): boolean {
	if (a === b) {
		return true;
	}

	return !!a && !!b && dayjs(a).valueOf() === dayjs(b).valueOf();
}

@Component({
	selector: `vp-availability-widget-input-date-range`,
	templateUrl: './availability-input-date-range.html',
	imports: [
		SVG_ICON_IMPORTS,
		AvailabilityWidgetInputComponent,
		CalendarComponent,
		CommonModule,
		LetDirective,
		StepperComponent,
		TranslatePipe,
		RouterLink,
	],
	providers: [CurrencyPipe, FlatpickrFacade],
})
export class AvailabilityWidgetInputDateRangeComponent implements OnChanges {
	private readonly currencyPipe = inject(CurrencyPipe);
	private readonly flatpickrFacade = inject(FlatpickrFacade);
	private readonly translate = inject(TranslateService);
	private readonly viewport = inject(ViewPortService);

	@Input() className = '';
	availability = input.required<AvailabilityDatesInterface>();
	config = input.required<Partial<FlatPickrBaseOptions>>();
	@Input() initialArrivalDate: Date;
	@Input() initialDepartureDate: Date;
	@Input() prettyDateFormat = 'dd D MMM YYYY';
	@Input() isLoading = false;
	@Input() jumpToFirstAvailableDate = false;
	@Input() stepperConfig = { useStepper: false, min: 1, max: undefined };
	@Input() numberOfNights: number | null = null;

	hideRangePrices = input<boolean>(false);
	resetDateRange = input<number>();
	bookingtoolHref = input<string | null>(undefined);
	bookingtoolQuery = input<Record<string, unknown>>({});
	openAvailabilityInNewTab = input<boolean>(false);
	ctaDisabled = input<boolean>(false);
	isExternalUrl = input<boolean>(false);

	@Input()
	set isOpen(value: boolean) {
		this._isOpen = value;
	}

	@Output() datePickerModelChanged = new EventEmitter<DatePickerInterface>();
	@Output() emitHasError = new EventEmitter<boolean>();
	@Output() emitMonthChange = new EventEmitter<string>();
	@Output() emitAvailabilityButton = new EventEmitter<Event>();
	@Output() emitBackButton = new EventEmitter<Event>();
	@Output() initialDatesNotAvailable = new EventEmitter<void>();
	@Output() emitIsPopoverOpen = new EventEmitter<boolean>();
	@Output() stepperUpdated = new EventEmitter<number>();

	currency = input('EUR');
	showPrices = input(false);

	isCtaDisabled = computed(
		() => this.ctaDisabled() || this.selectedDates().length < 2,
	);

	config$: Observable<Partial<FlatPickrBaseOptions>>;
	filteredCalendarData$: Observable<string[]>;
	selectedDates$: Observable<Date[]>;
	selectedDates: Signal<Date[]>;
	locale: string;
	prettyStartDate$: Observable<string>;
	prettyEndDate$: Observable<string>;
	prettyCombinedDates$: Observable<string>;

	recalculateCalendarData$ = new BehaviorSubject<void>(undefined);
	availability$ = new BehaviorSubject<AvailabilityDatesInterface>({});
	isSmallTablet$: Observable<boolean>;
	isSmallTablet: Signal<boolean>;
	_isOpen = false;

	cn = cn;

	private flatpickrInstance: FlatPickrInstance | undefined;

	// Store departure date range for selected arrival date to filter correctly
	// when navigating to months where the arrival date is not loaded
	private departureDateRange: DepartureDateRange | undefined;
	private arrivalDateKeyForStoredRange: string | undefined;

	constructor() {
		effect(() => {
			const trigger = this.resetDateRange();
			if (trigger !== undefined) {
				this.flatpickrFacade.clearFlatpickrInstance();
			}
		});

		effect(() => {
			const availability = this.availability();
			this.availability$.next(availability || {});
			this.recalculateCalendarData$.next();
		});

		this.locale =
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang();
		this.isSmallTablet$ = this.viewport.isSmallTablet$;
		this.isSmallTablet = toSignal(this.viewport.isSmallTablet$);
		this.config$ = this.adjustConfigToScreenSize();

		this.selectedDates$ = this.flatpickrFacade.selectSelectedDates$;
		this.selectedDates = toSignal(this.selectedDates$, {
			initialValue: [],
		});

		this.prettyStartDate$ = this.flatpickrFacade.selectStartDate$.pipe(
			map((startDate) => this.formatDate(startDate)),
		);

		this.prettyEndDate$ = this.flatpickrFacade.selectEndDate$.pipe(
			map((endDate) => this.formatDate(endDate)),
		);

		this.prettyCombinedDates$ = combineLatest([
			this.prettyStartDate$,
			this.prettyEndDate$,
		]).pipe(
			map(([prettyStartDate, prettyEndDate]) => {
				if (!prettyStartDate) {
					return '';
				}
				if (!prettyEndDate) {
					return prettyStartDate;
				}
				return `${prettyStartDate} - ${prettyEndDate}`;
			}),
		);

		this.filteredCalendarData$ = this.filterCalendarData();

		combineLatest([
			this.flatpickrFacade.selectStartDate$,
			this.flatpickrFacade.selectEndDate$,
		])
			.pipe(
				takeUntilDestroyed(),
				tap(([arrivalDate, departureDate]) => {
					this.datePickerModelChanged.emit({
						arrivalDate,
						departureDate,
					});
				}),
			)
			.subscribe();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['numberOfNights']) {
			this.flatpickrFacade.setNights(this.numberOfNights);
			this.flatpickrFacade.setEndDateAccordingToNights();
		}

		if (
			changes['config'] &&
			!changes['config'].firstChange &&
			this.flatpickrInstance
		) {
			this.syncDateBoundToCalendar('minDate', changes['config']);
			this.syncDateBoundToCalendar('maxDate', changes['config']);
		}

		if (this.shouldValidateDates(changes)) {
			this.validateAndApplyDates();
		}
	}

	popoverStateChanged(isOpen: boolean) {
		this._isOpen = isOpen;
		this.emitIsPopoverOpen.emit(this._isOpen);

		if (isOpen && this.jumpToFirstAvailableDate) {
			const [firstAvailableDate] = Object.keys(
				this.availability() ?? {},
			).sort((a, b) => a.localeCompare(b));

			if (firstAvailableDate) {
				this.flatpickrInstance.jumpToDate(firstAvailableDate, true);
			}
		}
	}

	isDepartureDateAvailable(arrivalDate: Date, departureDate: Date): boolean {
		const arrivalDateKey = this.formatDate(arrivalDate, 'YYYY-MM-DD');
		const departureDateKey = this.formatDate(departureDate, 'YYYY-MM-DD');
		return !!this.availability()?.[arrivalDateKey]?.[departureDateKey];
	}

	formatDate(
		date: string | Date | dayjs.Dayjs | undefined,
		format = this.prettyDateFormat,
	): string {
		return date ? `${dayjs(date).format(format)}` : '';
	}

	onDatePickerEmitter(event: FlatPickrEvent): void {
		if (event.type === FlatPickrEventType.OnReady) {
			this.flatpickrInstance = event.instance;
			this.setInitialDates(event.instance);
		}

		if (event.type === FlatPickrEventType.OnValueUpdate) {
			this.handleDateSelection(event);
			this.checkForInvalidDates(event.selectedDates);
			this.closePopoverIfComplete(event.selectedDates);
		}
	}

	onNumberOfNightsChange(numberOfNights: number) {
		this.numberOfNights = numberOfNights;
		this.stepperUpdated.emit(numberOfNights);
	}

	onEmitMonthChange(newDate: string) {
		this.emitMonthChange.emit(newDate);
	}

	onEmitHasError(hasError: boolean) {
		this.emitHasError.emit(hasError);
	}

	onEmitBackButton(event: Event) {
		this.popoverStateChanged(false);
		this.emitBackButton.emit(event);
	}

	onSubmitButtonClick(event: Event) {
		this.emitAvailabilityButton.emit(event);
	}

	addPricingToDayElement({
		dayElement,
		instance,
	}: {
		dayElement: HTMLElement;
		instance: FlatPickrInstance;
	}) {
		const formattedDate = dayjs(dayElement['dateObj']).format('YYYY-MM-DD');
		const date = this.availability()?.[formattedDate];

		if (date && this.showPrices()) {
			const checkoutDate = Object.keys(date)[0];

			if (checkoutDate && date[checkoutDate]?.price > 0) {
				const absolutePrice = date[checkoutDate].price / 100;
				const price = this.currencyPipe.transform(
					absolutePrice,
					this.currency(),
					'symbol-narrow',
					'1.0-0',
					this.locale,
				);

				addOptionalPricing(instance, dayElement, price.toString());
			}
		}
	}

	private syncDateBoundToCalendar(
		bound: 'minDate' | 'maxDate',
		change: SimpleChange,
	): void {
		const next = change.currentValue?.[bound];
		const previous = change.previousValue?.[bound];

		if (isSameInstant(next, previous)) {
			return;
		}

		this.flatpickrInstance.set(bound, next);
	}

	private shouldValidateDates(changes: SimpleChanges): boolean {
		const hasInitialDateChanges =
			changes['initialArrivalDate'] || changes['initialDepartureDate'];

		const availability = this.availability();
		return (
			hasInitialDateChanges &&
			!!availability &&
			Object.keys(availability).length > 0 &&
			this.areDatesValid(
				this.initialArrivalDate,
				this.initialDepartureDate,
			)
		);
	}

	private validateAndApplyDates(): void {
		const correctedDates = this.validateAndCorrectDates(
			this.initialArrivalDate,
			this.initialDepartureDate,
		);

		if (!correctedDates) {
			return;
		}

		this.flatpickrFacade.setDatesAndUpdateInCalendar(correctedDates);

		const datesWereCorrected =
			correctedDates[0] !== this.initialArrivalDate ||
			correctedDates[1] !== this.initialDepartureDate;

		if (datesWereCorrected) {
			this.datePickerModelChanged.emit({
				arrivalDate: correctedDates[0],
				departureDate: correctedDates[1],
			});
		}
	}

	private validateAndCorrectDates(
		arrivalDate: Date,
		departureDate: Date,
	): [Date, Date] | null {
		if (!this.stepperConfig?.useStepper) {
			return [arrivalDate, departureDate];
		}

		const minNights = this.stepperConfig.min ?? 1;
		const maxNights = this.stepperConfig.max;
		const calculatedNights = dayjs(departureDate).diff(arrivalDate, 'days');

		if (
			calculatedNights >= minNights &&
			(!maxNights || calculatedNights <= maxNights) &&
			this.isDepartureDateAvailable(arrivalDate, departureDate)
		) {
			return [arrivalDate, departureDate];
		}

		const arrivalDateKey = this.formatDate(arrivalDate, 'YYYY-MM-DD');
		const availableDepartures = this.availability()?.[arrivalDateKey];

		if (availableDepartures) {
			const validDeparture = this.findValidDeparture(
				arrivalDate,
				availableDepartures,
				minNights,
				maxNights,
			);
			if (validDeparture) {
				return [arrivalDate, validDeparture];
			}
		}

		return this.findFirstValidDatePair(minNights, maxNights);
	}

	private findValidDeparture(
		arrivalDate: Date,
		availableDepartures: Record<string, unknown>,
		minNights: number,
		maxNights?: number,
	): Date | null {
		for (const departureDateKey in availableDepartures) {
			const nights = dayjs(departureDateKey).diff(arrivalDate, 'days');

			if (nights >= minNights && (!maxNights || nights <= maxNights)) {
				return dayjs(departureDateKey).toDate();
			}
		}

		return null;
	}

	private findFirstValidDatePair(
		minNights: number,
		maxNights?: number,
	): [Date, Date] | null {
		const availability = this.availability();
		if (!availability) {
			return null;
		}

		for (const arrivalDateKey in availability) {
			const arrivalDate = dayjs(arrivalDateKey).toDate();
			const validDeparture = this.findValidDeparture(
				arrivalDate,
				availability[arrivalDateKey],
				minNights,
				maxNights,
			);

			if (validDeparture) {
				return [arrivalDate, validDeparture];
			}
		}

		return null;
	}

	private areDatesValid(arrivalDate: Date, departureDate: Date): boolean {
		const isArrivalValid =
			arrivalDate instanceof Date && !Number.isNaN(arrivalDate.getTime());
		const isDepartureValid =
			departureDate instanceof Date &&
			!Number.isNaN(departureDate.getTime());

		return (
			isArrivalValid &&
			isDepartureValid &&
			dayjs(departureDate).isAfter(arrivalDate, 'day')
		);
	}

	private setInitialDates(instance: FlatPickrInstance): void {
		if (
			!this.areDatesValid(
				this.initialArrivalDate,
				this.initialDepartureDate,
			)
		) {
			return;
		}

		const dates = [this.initialArrivalDate, this.initialDepartureDate];
		instance.setDate(dates, false);
		this.flatpickrFacade.setDates(dates);
		this.flatpickrFacade.setStartDate(dates[0]);
		this.flatpickrFacade.setEndDate(dates[1]);
		this.flatpickrFacade.setNights(dayjs(dates[1]).diff(dates[0], 'day'));
	}

	private adjustConfigToScreenSize(): Observable<
		Partial<FlatPickrBaseOptions>
	> {
		return this.isSmallTablet$.pipe(
			distinctUntilChanged(),
			map((isSmallTablet) => {
				const config = this.config();
				return {
					...config,
					showMonths: isSmallTablet ? 1 : config.showMonths,
				};
			}),
			tap((config) => {
				this.flatpickrFacade.updateShowMonths(config.showMonths);
			}),
		);
	}

	private handleDateSelection(event: FlatPickrEvent): void {
		if (this.numberOfNights && this.numberOfNights > 0) {
			this.flatpickrFacade.setEndDate(null);
			this.flatpickrFacade.setStartDate(event.selectedDates[0]);
			this.flatpickrFacade.setNights(this.numberOfNights);
			this.flatpickrFacade.setEndDateAccordingToNights();

			if (!this.isSmallTablet()) {
				this.popoverStateChanged(false);
			}
		} else {
			this.flatpickrFacade.setDatesAndUpdateNights(event.selectedDates);
			this.recalculateCalendarData$.next();
		}
	}

	private closePopoverIfComplete(selectedDates: Date[]): void {
		if (selectedDates.length > 1 && !this.isSmallTablet()) {
			this.popoverStateChanged(false);
		}
	}

	private checkForInvalidDates(selectedDates: Date[]): void {
		if (selectedDates.length === 0) {
			return;
		}

		const [arrivalDate, departureDate] = selectedDates;
		const { minDate, maxDate } = this.config();
		const isArrivalOutOfBounds =
			minDate && dayjs(arrivalDate).isBefore(dayjs(minDate), 'day');
		const isDepartureOutOfBounds =
			maxDate &&
			selectedDates.length > 1 &&
			dayjs(departureDate).isAfter(dayjs(maxDate), 'day');

		// Availability-based checks are only meaningful once availability has
		// actually loaded. An empty set means "not known yet"
		const availability = this.availability();
		const availabilityLoaded =
			!!availability && Object.keys(availability).length > 0;

		// Check if arrival date is valid (has at least one departure date after the arrival date)
		const arrivalDateKey = this.formatDate(arrivalDate, 'YYYY-MM-DD');
		const isArrivalInvalid =
			availabilityLoaded &&
			!isArrivalDateValid(availability, arrivalDateKey);

		const isDepartureInvalid =
			availabilityLoaded &&
			selectedDates.length > 1 &&
			!isDepartureDateAvailableHelper(
				availability,
				arrivalDateKey,
				this.formatDate(departureDate, 'YYYY-MM-DD'),
			);

		if (
			isArrivalOutOfBounds ||
			isDepartureOutOfBounds ||
			isArrivalInvalid ||
			isDepartureInvalid
		) {
			this.flatpickrFacade.clearFlatpickrInstance();
		}
	}

	filterCalendarData(): Observable<string[]> {
		return combineLatest([
			this.recalculateCalendarData$,
			this.selectedDates$,
			this.availability$,
		]).pipe(
			map(([_, selectedDates, availability]) => {
				if (!availability || Object.keys(availability).length === 0) {
					return [];
				}

				const availableDates = new Set<string>();

				if (selectedDates.length === 0) {
					this.addAllArrivalDates(availability, availableDates);
				} else if (selectedDates.length === 1) {
					this.filterForDepartureDates(
						availability,
						selectedDates[0],
						availableDates,
					);
				} else {
					this.filterForBothDates(
						availability,
						selectedDates[0],
						availableDates,
					);
				}

				return Array.from(availableDates);
			}),
		);
	}

	private filterForDepartureDates(
		availability: AvailabilityDatesInterface,
		arrivalDate: Date,
		availableDates: Set<string>,
	): void {
		const arrivalDateKey = this.formatDate(arrivalDate, 'YYYY-MM-DD');
		const availableDepartures = availability[arrivalDateKey];

		if (availableDepartures) {
			this.addValidDepartureDates(
				availableDepartures,
				arrivalDate,
				availableDates,
			);
		}
	}

	private filterForBothDates(
		availability: AvailabilityDatesInterface,
		arrivalDate: Date,
		availableDates: Set<string>,
	): void {
		// Allow changing arrival date
		this.addAllArrivalDates(availability, availableDates);

		// Add valid departure dates for selected arrival date
		const arrivalDateKey = this.formatDate(arrivalDate, 'YYYY-MM-DD');
		const availableDepartures = availability[arrivalDateKey];

		if (availableDepartures) {
			this.addValidDepartureDates(
				availableDepartures,
				arrivalDate,
				availableDates,
				availability,
			);
		}
	}

	private addAllArrivalDates(
		availability: AvailabilityDatesInterface,
		availableDates: Set<string>,
	): void {
		this.clearStoredRange();
		Object.keys(availability).forEach((dateKey) => {
			const availableDepartures = availability[dateKey];
			// Only add arrival dates that have at least one departure date after the arrival date
			const hasValidDeparture =
				availableDepartures &&
				Object.keys(availableDepartures).some((departureDateKey) => {
					return dayjs(departureDateKey).isAfter(dateKey, 'day');
				});

			if (hasValidDeparture) {
				availableDates.add(dateKey);
			}
		});
	}

	private addValidDepartureDates(
		availableDepartures: Record<string, { price?: number }>,
		arrivalDate: Date,
		availableDates: Set<string>,
		availability?: AvailabilityDatesInterface,
	): void {
		Object.keys(availableDepartures).forEach((departureDate) => {
			// Add departure dates that are after the arrival date
			// If availability is provided, only add departure dates that are also valid arrival dates
			// Empty objects {} are valid - they just mean no price data
			if (dayjs(departureDate).isAfter(arrivalDate, 'day')) {
				if (
					!availability ||
					isArrivalDateValid(availability, departureDate)
				) {
					availableDates.add(departureDate);
				}
			}
		});
	}

	private clearStoredRange(): void {
		this.departureDateRange = undefined;
		this.arrivalDateKeyForStoredRange = undefined;
	}
}
